import { Avatar, Box, Button, Typography } from '@mui/material';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { InputKit } from '../../../components/UI';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CameraPlus } from '../../../assets/Icons/camera-plus';
import { AuthService } from '../../../Service/AuthService';
import { useAppDispatch } from '../../../store/hooks';
import { LocalStorageService } from '../../../Service/LocalStorageService';
import { setAuthUser, setToken } from '../../../store/user/user-slice';
import { createNotification } from '../../../store/notifications/notification-slice';

const MAX_FILE_SIZE = 5000000;
const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
];

const schema = z
  .object({
    photo: z
      .any()
      .refine(file => file?.size <= MAX_FILE_SIZE, `Max image size is 5MB.`)
      .refine(
        file => ACCEPTED_IMAGE_TYPES.includes(file?.type),
        'Only .jpg, .jpeg, .png and .webp formats are supported.'
      )
      .optional(),
    name: z.string().min(1),
    lastName: z.string().min(1),
    email: z
      .string({ required_error: 'Email is required' })
      .refine(
        value => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value),
        'Invalid email address'
      ),
    password: z
      .string()
      .refine(
        value =>
          /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*]).{8,}$/.test(value),
        'Minimum eight characters, at least one number and one special character'
      ),
    confirmPassword: z.string().min(8),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

export const RegisterMember = () => {
  const [query] = useSearchParams();
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();
  const methods = useForm({
    defaultValues: Object.fromEntries(query.entries()),
    resolver: zodResolver(schema),
  });

  const registerMember = async (values: FieldValues) => {
    try {
      const { data }: any = await AuthService.registerMember(values);

      LocalStorageService.setItem('accessToken', data.accessToken);

      appDispatch(setToken(data.accessToken));
      appDispatch(setAuthUser(data));

      navigate('/home');
    } catch (error: any) {
      appDispatch(
        createNotification({
          type: 'error',
          message: error.message,
        })
      );
    }
  };

  const onUploadPhoto = (e: any) => {
    methods.setValue('photo', e.target.files[0]);
  };

  const getImage = () => {
    const photo: any = methods.watch('photo');

    return photo ? URL.createObjectURL(photo) : '';
  };

  return (
    <FormProvider {...methods}>
      <Box
        sx={{
          maxWidth: '100%',
          width: '100%',
          display: 'flex',
          padding: '20px',
          justifyContent: 'center',
          alignItems: 'center',
          '@media (max-width: 768px)': {
            alignItems: 'unset',
          },
        }}
      >
        <form
          style={{
            maxWidth: '472px',
            width: '100%',
          }}
          onSubmit={methods.handleSubmit(registerMember)}
        >
          <Box display={'flex'} flexDirection={'column'} gap={'24px'}>
            <Typography variant='h1'>
              Create your{' '}
              <span
                style={{
                  color: '#3293DB',
                }}
              >
                K2
              </span>{' '}
              account
            </Typography>
            <Box display={'flex'} gap={'32px'} alignItems={'center'}>
              <Avatar
                sx={{
                  width: '94px',
                  height: '94px',
                  border: '1px solid #E3DDE6',
                  background: 'white',
                }}
                src={getImage()}
              >
                <CameraPlus />
              </Avatar>

              <Box display={'flex'} flexDirection={'column'} gap={'12px'}>
                <Button
                  sx={{
                    background: '#3293DB',
                    color: '#fff',
                    paddingX: '14px',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                  variant='contained'
                  component='label'
                >
                  Upload Photo
                  <input
                    onChange={onUploadPhoto}
                    hidden
                    accept='image/*'
                    type='file'
                  />
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: '24px' }}>
              <InputKit name='name' placeholder='First name' />
              <InputKit name='lastName' placeholder='Last name' />
            </Box>
            <InputKit name='email' placeholder='Email' />
            <InputKit type='password' name='password' placeholder='Password' />
            <InputKit
              type='password'
              name='confirmPassword'
              placeholder='Confirm password'
            />
            <Button variant='contained' type='submit'>
              Register
            </Button>
            <Typography
              fontSize={'16px'}
              variant='body1'
              color={'#655C68'}
              fontWeight={400}
            >
              Do you have an account?{' '}
              <Link
                style={{
                  color: '#3293DB',
                  fontWeight: 500,
                  textDecoration: 'none',
                }}
                to={'/'}
              >
                Log in
              </Link>
            </Typography>
          </Box>
        </form>
      </Box>
    </FormProvider>
  );
};
